import { useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { fileDownload, externalWindow } from 'utils';
import { routes } from 'const';
import appConfig from 'config';
import api from 'client';
import { useOrganization } from 'hooks';

export default (appointmentId) => {
  const { t } = useTranslation('appointment');
  const [downloadingList, setDownloadingList] = useState([]);
  const queryClient = useQueryClient();
  const { data: organization } = useOrganization();

  const download = (attachmentId, attachmentName) => {
    setDownloadingList((list) => [...list, attachmentId]);
    return api
      .get(`/apis/v2/appointments/${appointmentId}/documents/${attachmentId}`, { responseType: 'blob' })
      .then((response) => {
        fileDownload(response.data, attachmentName, { newWindow: true, mime: response.headers['content-type'] });
      })
      .catch((err) => {
        toast.error(() => err.message);
      })
      .finally(() => {
        setDownloadingList((list) => list.filter((item) => item !== attachmentId));
      });
  };

  const upload = async ({ attachments = [] }) => {
    const data = new FormData();
    const fileList = Array.from(attachments);
    if (fileList.length > 0) {
      fileList.forEach((file) => {
        data.append('files[]', file);
      });
    }
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    return api.post(`/apis/v2/appointments/${appointmentId}/documents`, data, config).catch((err) => {
      toast.error(() => err.response?.data?.meta?.attachment || err.message);
    });
  };

  const joinOnlineAppointment = async () => {
    try {
      const { data } = await api.post(`/apis/v2/video_session_tokens`, { appointmentId });
      const { id, attributes } = data.data;
      const { wherebyUrl } = attributes;
      const { wherebyIntegration } = organization.features;
      let url;
      if (wherebyIntegration && wherebyUrl) {
        url = `${window.location.origin}/portal/video-session/${id}${organization ? `?oid=${organization.id}` : ''}`;
      } else if (appConfig.useReactVideoSessions) {
        url = `${routes.videoSession}/${id}`;
      } else {
        url = `${window.location.origin}/portal/video-session/${id}${organization ? `?oid=${organization.id}` : ''}`;
      }
      externalWindow.launchNewTab(url, {});
    } catch (err) {
      toast.error(() => err.message);
    }
  };
  const confirmAppointment = async () => {
    try {
      await api.put(`/apis/v2/appointments/${appointmentId}/confirm`, {});
      toast(t('confirmation_status.confirmed'));
    } catch (err) {
      toast.error(() => err.message);
    }
  };
  const cancelAppointment = (cancellationReason = '') =>
    api.put(`/apis/v2/appointments/${appointmentId}/cancel`, {
      cancellation_reason: cancellationReason,
    });

  const cancelMutation = useMutation(cancelAppointment, {
    onSuccess: () => {
      toast(t('request_sent'));
      queryClient.invalidateQueries(['GET', `/apis/v2/appointments/${appointmentId}`, {}]);
      queryClient.invalidateQueries(['GET', `/apis/v2/appointments`, {}]);
    },
    onError: () => {
      toast.error(t('appointment_could_not_be_cancelled'));
      queryClient.invalidateQueries(['GET', `/apis/v2/appointments/${appointmentId}`, {}]);
      queryClient.invalidateQueries(['GET', `/apis/v2/appointments`, {}]);
    },
  });

  const confirmMutation = useMutation(confirmAppointment, {
    onSuccess: () => {
      queryClient.invalidateQueries(['GET', `/apis/v2/appointments/${appointmentId}`, {}]);
      queryClient.invalidateQueries(['GET', `/apis/v2/appointments`, {}]);
    },
  });

  const mutation = useMutation(upload, {
    onSuccess: () => {
      queryClient.invalidateQueries(['GET', `/apis/v2/appointments/${appointmentId}`, {}]);
    },
  });

  return {
    upload: mutation.mutate,
    download,
    downloadingList,
    joinOnlineAppointment,
    confirmAppointment: confirmMutation.mutate,
    cancelAppointment: cancelMutation.mutateAsync,
  };
};
